import { Instrument, InstrumentKind } from '@/types/generated/common/types'

/**
 * Spot symbol whose BASE currency is the given balance currency (e.g. BTC → BTC_USDC).
 * A currency with no such pair (e.g. USDC itself, only ever a quote currency) has no
 * entry in the returned map.
 */
export function buildSpotPairMap(instruments: Instrument[]): Map<string, string> {
  const spotTickerByBaseCurrency = new Map<string, string>()
  instruments.forEach((inst) => {
    if (inst.kind !== InstrumentKind.INSTRUMENT_KIND_SPOT) return
    const baseCurrency = inst.baseCurrency?.toUpperCase()
    const quoteCurrency = inst.quoteCurrency?.toUpperCase()
    if (!baseCurrency || !quoteCurrency) return
    if (quoteCurrency === 'USDC' || quoteCurrency === 'USDT') {
      if (!spotTickerByBaseCurrency.has(baseCurrency) || quoteCurrency === 'USDC') {
        spotTickerByBaseCurrency.set(baseCurrency, inst.symbol)
      }
    }
  })
  return spotTickerByBaseCurrency
}

/** Resolve a balance's chart without using a fallback market to value the balance itself. */
export function resolveSpotBalanceChartInstrument(
  instruments: Map<string, Instrument>,
  currency: string
): Instrument | undefined {
  const upper = currency.toUpperCase()
  let direct: Instrument | undefined
  let bitcoinQuote: Instrument | undefined
  let bitcoinSpot: Instrument | undefined
  for (const instrument of instruments.values()) {
    if (instrument.kind !== InstrumentKind.INSTRUMENT_KIND_SPOT) continue
    const base = instrument.baseCurrency?.toUpperCase()
    const quote = instrument.quoteCurrency?.toUpperCase()
    if (base === upper) {
      if (
        !direct ||
        quote === 'USDC' ||
        (quote === 'USDT' && direct.quoteCurrency?.toUpperCase() !== 'USDC')
      ) {
        direct = instrument
      }
    }
    // Hyperliquid's Unit-bridged BTC spot market is catalogued as UBTC.
    if (base === 'BTC' || base === 'UBTC') {
      if (quote === upper && (!bitcoinQuote || base === 'BTC')) bitcoinQuote = instrument
      if (
        !bitcoinSpot ||
        quote === 'USDC' ||
        (quote === 'USDT' && bitcoinSpot.quoteCurrency?.toUpperCase() !== 'USDC')
      ) {
        bitcoinSpot = instrument
      }
    }
  }
  return direct ?? bitcoinQuote ?? bitcoinSpot
}
